import { walkSections } from '../../render-core/sectionTree';
import type { FieldMeta } from '../api/types';
import {
  getRecordCells,
  getFieldMetaList,
  getLinkedRecords,
  getRecordIds,
  getCellAttachmentUrls,
} from '../api/bitable';
import QRCode from 'qrcode';
import { code128ToSvgDataUrl } from '../utils/barcode';
import { findFieldByRef } from './fieldMatch';
import { formatUnknownValue } from '../utils/formatValue';
import type { AttachmentItem, ContractTemplate, ContractRenderData, LinkedTableData } from './types';

function collectMainFieldRefs(template: ContractTemplate): string[] {
  const refs = new Set<string>();
  for (const s of walkSections(template.sections)) {
    switch (s.type) {
      case 'header':
        for (const m of s.content.matchAll(/\{\{([^}]+)\}\}/g)) {
          refs.add(m[1]);
        }
        break;
      case 'info_grid':
        for (const item of s.items) {
          if (item.fieldId) refs.add(item.fieldId);
        }
        break;
      case 'text_block':
        for (const m of s.content.matchAll(/\{\{([^}]+)\}\}/g)) {
          refs.add(m[1]);
        }
        break;
      case 'free_table':
        for (const row of s.rows) {
          for (const cell of row.cells) {
            for (const m of cell.content.matchAll(/\{\{([^}]+)\}\}/g)) {
              refs.add(m[1]);
            }
          }
        }
        // 动态明细的样板行要读关联字段
        if (s.repeat?.linkFieldId) refs.add(s.repeat.linkFieldId);
        break;
      case 'detail_table':
        refs.add(s.linkFieldId);
        break;
      case 'attachment':
        if (s.fieldId) refs.add(s.fieldId);
        break;
      case 'code':
        if (s.fieldId) refs.add(s.fieldId);
        break;
      case 'rich_text':
        if (s.fieldId) refs.add(s.fieldId);
        break;
      case 'signature':
        for (const p of s.parties) {
          for (const r of p.rows) {
            if (r.fieldId) refs.add(r.fieldId);
          }
        }
        break;
    }
  }
  // 条件显隐规则引用的字段
  if (template.visibility) {
    for (const rule of Object.values(template.visibility)) {
      if (rule?.fieldId) refs.add(rule.fieldId);
    }
  }
  return [...refs];
}

function resolveRefsToFieldIds(refs: string[], fieldMetas: FieldMeta[]): string[] {
  const ids = new Set<string>();
  for (const rawRef of refs) {
    // 去掉格式化修饰符 {{字段名|大写}} → 字段名
    const ref = rawRef.includes('|') ? rawRef.slice(0, rawRef.lastIndexOf('|')).trim() : rawRef;
    const m = findFieldByRef(fieldMetas, ref);
    if (m) ids.add(m.id);
  }
  return [...ids];
}

// 示例数据构造是纯函数，已随渲染器迁到 render-core；这里转发旧路径。
export { buildSampleContractData } from '../../render-core/sampleData';

const ATT_PROXY_BASE = 'https://printpro.xiaomiao.win/api/att';

/**
 * 把飞书的附件临时 URL 换成走我们自己 worker 的中转地址。
 *
 * 直连飞书域名会在三个地方断，表现就是文档里一个个破图：
 *   1. 临时 URL 只活几分钟，停留久一点再看/再打印就失效
 *   2. 插件是嵌在飞书里的**第三方 iframe**（我们在 print.xiaomiao.win），
 *      URL 若依赖飞书登录态，Chrome 现在默认拦第三方 Cookie
 *   3. CDN 可能按 referrer 拒绝非飞书来源
 * 服务端取图没有以上任何一条限制。
 *
 * 还顺带救了导出：pdfExporter 用 html2canvas 且开了 useCORS，飞书若不返回
 * CORS 头，图片会**整张加载失败**；代理会补上 ACAO 头。
 */
function toProxiedAttachmentUrl(url: string, fileName: string): string {
  // 空值和 data: 不用管（data: 本来就是内联的）
  if (!url || url.startsWith('data:')) return url;
  // n 只用于让代理按扩展名判定图片类型：飞书下载端点返回的多半是
  // application/octet-stream，直接透传会被 nosniff 挡成破图。
  return `${ATT_PROXY_BASE}?u=${encodeURIComponent(url)}&n=${encodeURIComponent(fileName)}`;
}

const FT_ATTACHMENT = 17;

/** 「本表多条记录」的行数上限。一张单印上千行不现实，多半是选错了范围。 */
const MAX_TABLE_RECORDS = 500;

type AttachmentCell = { token?: string; name?: string; type?: string };

/**
 * 把一个附件单元格的 token 换成可加载的（代理过的）图片地址。
 * 换不到 URL 时返回 null，调用方不挂 —— 渲染器自然退回文件名。
 */
async function resolveAttachmentCell(
  tableId: string,
  fieldId: string,
  recordId: string,
  raw: unknown,
): Promise<AttachmentItem[] | null> {
  if (!Array.isArray(raw) || raw.length === 0) return null;
  const cells = raw as AttachmentCell[];
  const tokens = cells.map((c) => c.token).filter((t): t is string => !!t);
  if (tokens.length === 0) return null;
  const urls = await getCellAttachmentUrls(tableId, fieldId, recordId, tokens);
  // urls 与 tokens 同序；cell 可能缺 token，按 token 反查而不是按下标
  const urlByToken = new Map(tokens.map((t, i) => [t, urls[i] || '']));
  const items: AttachmentItem[] = cells.map((c, i) => {
    const direct = (c.token && urlByToken.get(c.token)) || '';
    const name = c.name || `附件${i + 1}`;
    return {
      name,
      url: toProxiedAttachmentUrl(direct, name),
      // 代理失手时退回直连（渲染器会挂 onerror）—— 保证不比加代理之前更糟
      fallbackUrl: direct || undefined,
      mimeType: c.type || '',
      token: c.token || undefined,
      ref: { tableId, fieldId, recordId },
    };
  });
  return items.some((it) => it.url) ? items : null;
}

/**
 * 明细表格的附件列：把每行的 token 换成可加载的图片地址，挂到 record.attachments。
 *
 * getCellValue 只给 {token,name,type}，直接渲染只能印出文件名（用户看到的
 * 就是一列 image.png）。URL 同样要过我们的代理，原因见 toProxiedAttachmentUrl。
 * 换 URL 失败就不挂，渲染器自然退回文件名 —— 不许比之前更糟。
 */
/** 不取临时地址，只把附件的 token + 坐标挂上（给独立页的 /api/att/live 用）。 */
function attachTokensOnly(
  tableId: string,
  fieldMetas: FieldMeta[],
  columnFieldIds: string[],
  records: LinkedTableData['records'],
): void {
  const attFieldIds = columnFieldIds.filter((fid) => fieldMetas.find((m) => m.id === fid)?.type === FT_ATTACHMENT);
  if (attFieldIds.length === 0) return;
  for (const rec of records) {
    for (const fid of attFieldIds) {
      const raw = rec.cells[fid];
      if (!Array.isArray(raw) || raw.length === 0) continue;
      const items: AttachmentItem[] = (raw as AttachmentCell[])
        .filter((c) => !!c.token)
        .map((c, i) => ({
          name: c.name || `附件${i + 1}`,
          url: '',
          mimeType: c.type || '',
          token: c.token,
          ref: { tableId, fieldId: fid, recordId: rec.recordId },
        }));
      if (items.length) rec.attachments = { ...(rec.attachments ?? {}), [fid]: items };
    }
  }
}

async function attachLinkedImages(
  tableId: string,
  fieldMetas: FieldMeta[],
  columnFieldIds: string[],
  records: LinkedTableData['records'],
): Promise<void> {
  const attFieldIds = columnFieldIds.filter(
    (fid) => fieldMetas.find((m) => m.id === fid)?.type === FT_ATTACHMENT,
  );
  if (attFieldIds.length === 0) return;

  const jobs: Promise<void>[] = [];
  for (const rec of records) {
    for (const fid of attFieldIds) {
      jobs.push(
        resolveAttachmentCell(tableId, fid, rec.recordId, rec.cells[fid]).then((items) => {
          if (items) rec.attachments = { ...(rec.attachments ?? {}), [fid]: items };
        }),
      );
    }
  }
  await Promise.all(jobs);
}

/**
 * 把当前记录所有关联字段指向的子表记录都取一遍，键用**关联字段 id**。
 *
 * 给独立页的快照用：那边没有 SDK，用户在全屏里新配一张明细表时，模板里
 * 原本没有这个区块、快照里也就没有它的行。渲染器找不到 linkedRecords[区块 id]
 * 会退回 linkedRecords[关联字段 id]，所以这里按字段 id 预备一份。
 */
export async function prefetchAllLinks(
  mainRecord: Record<string, unknown>,
  mainFieldMetas: FieldMeta[],
  maxRows = 30,
  withImages = false,
): Promise<Record<string, LinkedTableData>> {
  const out: Record<string, LinkedTableData> = {};
  const jobs = mainFieldMetas
    .filter((f) => f.type === 18 || f.type === 21)
    .map(async (f) => {
      const v = mainRecord[f.id] as { recordIds?: string[]; tableId?: string } | undefined;
      const tableId = v?.tableId || (f as unknown as { property?: { tableId?: string } }).property?.tableId;
      if (!v?.recordIds?.length || !tableId) return;
      try {
        const fieldMetas = await getFieldMetaList(tableId);
        const ids = v.recordIds.slice(0, maxRows);
        const columnFieldIds = fieldMetas.map((m) => m.id);
        const records = await getLinkedRecords(tableId, ids, columnFieldIds);
        // 附件链接一条一次调用，预取时默认不取 —— 这是打开编辑器最慢的一段。
        // 只把 token 和坐标带上，独立页会让 worker 按 token 现取地址。
        if (withImages) await attachLinkedImages(tableId, fieldMetas, columnFieldIds, records);
        else attachTokensOnly(tableId, fieldMetas, columnFieldIds, records);
        out[f.id] = { tableId, fieldMetas, records };
      } catch {
        /* 某个关联取不到就跳过，不挡其他的 */
      }
    });
  await Promise.all(jobs);
  return out;
}

export async function fetchContractData(
  mainTableId: string,
  recordId: string,
  template: ContractTemplate,
  mainFieldMetas: FieldMeta[],
  /** 「本表多条记录」的表格要按视图取记录范围；不给则退回全表。 */
  viewId?: string,
): Promise<ContractRenderData> {
  const refs = collectMainFieldRefs(template);
  const neededFieldIds = resolveRefsToFieldIds(refs, mainFieldMetas);

  const mainRecord = neededFieldIds.length > 0
    ? await getRecordCells(mainTableId, recordId, neededFieldIds)
    : {};

  // 主记录里被引用到的附件字段（附件区块 / 自由表格 {{产品图片}} 等）统一解析一次，
  // 按 fieldId 归档；下面的附件区块和渲染器都从这里取，不重复调 SDK。
  const mainAttachments: Record<string, AttachmentItem[]> = {};
  await Promise.all(
    neededFieldIds
      .filter((fid) => mainFieldMetas.find((m) => m.id === fid)?.type === FT_ATTACHMENT)
      .map(async (fid) => {
        const items = await resolveAttachmentCell(mainTableId, fid, recordId, mainRecord[fid]);
        if (items) mainAttachments[fid] = items;
      }),
  );

  const linkedRecords: Record<string, LinkedTableData> = {};

  // 明细表格与「自由表格的动态明细」都要读关联子表，归一成同一组任务
  const linkJobs: Array<{ sectionId: string; linkRef: string; fallbackTableId: string; columnRefs: string[] }> = [];
  const recordJobs: Array<{ sectionId: string; range: 'visible' | 'selected' | 'all'; columnRefs: string[] }> = [];
  for (const section of walkSections(template.sections)) {
    if (section.type === 'detail_table' && section.linkFieldId && section.linkedTableId) {
      linkJobs.push({
        sectionId: section.id,
        linkRef: section.linkFieldId,
        fallbackTableId: section.linkedTableId,
        columnRefs: section.columns.map((c) => c.fieldId),
      });
    } else if (section.type === 'free_table' && section.repeat) {
      const rep = section.repeat;
      const src = rep.source ?? 'link';
      if (src === 'link' && rep.linkFieldId) {
        linkJobs.push({
          sectionId: section.id,
          linkRef: rep.linkFieldId,
          fallbackTableId: '',
          columnRefs: Object.values(rep.columnFields || {}),
        });
      } else if (src === 'records') {
        // 「本表多条记录」：一行 = 本表的另一条记录（多条记录摊成一张清单）。
        // 归档到同一个 linkedRecords[区块 id]，渲染层完全不用感知来源差异。
        recordJobs.push({
          sectionId: section.id,
          range: rep.recordRange ?? 'visible',
          columnRefs: Object.values(rep.columnFields || {}),
        });
      }
    }
  }

  for (const job of linkJobs) {
    // mainRecord 的键是**字段 id**（neededFieldIds 已经把 ref 解析过一遍），
    // 而 linkRef 可能是字段**名**——内置模板库就是按名称绑定的。
    // 直接拿它当键查，库模板里的明细表格永远取不到数据。
    const linkFieldId = findFieldByRef(mainFieldMetas, job.linkRef)?.id;
    const linkValue = linkFieldId ? mainRecord[linkFieldId] : undefined;
    if (!linkValue || typeof linkValue !== 'object') continue;

    const openLink = linkValue as { recordIds?: string[]; tableId?: string };
    const recordIds = openLink.recordIds;
    if (!recordIds || recordIds.length === 0) continue;

    const linkedTableId = openLink.tableId || job.fallbackTableId;
    if (!linkedTableId) continue;

    const fieldMetas = await getFieldMetaList(linkedTableId);
    // 自由表格的动态明细按字段**名**配列，这里统一解析成真实 id 再取值
    const columnFieldIds = job.columnRefs
      .map((ref) => findFieldByRef(fieldMetas, ref)?.id)
      .filter((id): id is string => !!id);
    const records = await getLinkedRecords(linkedTableId, recordIds, columnFieldIds);

    await attachLinkedImages(linkedTableId, fieldMetas, columnFieldIds, records);

    // 键用区块 id，不是关联字段 id —— 同一个关联字段可以配多张明细表（列不同）
    linkedRecords[job.sectionId] = { tableId: linkedTableId, fieldMetas, records };
  }

  // 「本表多条记录」的表格：取本表的记录，塞进同一个 linkedRecords 结构
  for (const job of recordJobs) {
    try {
      const ids = await getRecordIds(mainTableId, viewId ?? '', job.range);
      if (ids.length === 0) continue;
      const capped = ids.slice(0, MAX_TABLE_RECORDS);
      const columnFieldIds = job.columnRefs
        .map((ref) => findFieldByRef(mainFieldMetas, ref)?.id)
        .filter((id): id is string => !!id);
      const records = await getLinkedRecords(mainTableId, capped, columnFieldIds);
      await attachLinkedImages(mainTableId, mainFieldMetas, columnFieldIds, records);
      linkedRecords[job.sectionId] = {
        tableId: mainTableId,
        fieldMetas: mainFieldMetas,
        records,
      };
    } catch {
      /* 取不到就当这张表没数据，不挡整份文档 */
    }
  }

  // 附件区块：把 cell 里的 token 换成短时效下载 URL（渲染时现取现用）
  // 注意 URL 会再包一层我们自己的代理，原因见 toProxiedAttachmentUrl。
  const attachments: Record<string, AttachmentItem[]> = {};
  for (const section of walkSections(template.sections)) {
    if (section.type !== 'attachment' || !section.fieldId) continue;
    // 同上：section.fieldId 可能是字段名，必须先解析成真实 id —— 否则既查不到
    // 单元格，传给 getCellAttachmentUrls 的 fieldId 也是错的。
    const attFieldId = findFieldByRef(mainFieldMetas, section.fieldId)?.id;
    if (!attFieldId) continue;
    const items = mainAttachments[attFieldId];
    if (items) attachments[section.id] = items;
  }

  // 二维码/条形码：解析内容 → 预生成图片 data URL（渲染器同步取用）
  const codes: Record<string, string> = {};
  for (const section of walkSections(template.sections)) {
    if (section.type !== 'code') continue;
    let value = section.staticValue || '';
    if (section.fieldId) {
      const matched = findFieldByRef(mainFieldMetas, section.fieldId);
      if (matched) {
        value = formatValueForCode(mainRecord[matched.id]);
      }
    }
    if (!value) continue;
    try {
      if (section.codeType === 'qr') {
        codes[section.id] = await QRCode.toDataURL(value, {
          width: Math.max(80, section.sizePx || 120),
          margin: 1,
        });
      } else {
        codes[section.id] = code128ToSvgDataUrl(value, {
          height: Math.max(30, section.sizePx || 60),
          showText: section.showText,
        });
      }
    } catch {
      /* 内容无法编码就跳过该码 */
    }
  }

  return { mainRecord, mainFieldMetas, linkedRecords, attachments, mainAttachments, codes };
}

/** 把单元格值转成可编码字符串（简单取文本，数组/对象取首个可读值）。 */
function formatValueForCode(raw: unknown): string {
  if (raw == null) return '';
  if (typeof raw === 'string' || typeof raw === 'number') return String(raw);
  if (Array.isArray(raw)) {
    return raw
      .map((x) =>
        typeof x === 'object' && x && 'text' in x
          ? String((x as { text: unknown }).text)
          : formatUnknownValue(x),
      )
      .join('');
  }
  if (typeof raw === 'object' && 'text' in (raw as object)) return String((raw as { text: unknown }).text);
  return formatUnknownValue(raw);
}
